import { ReactElement, useCallback } from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	FormattedText,
	Space,
	TransparentButtonList,
	Typography
} from "client/Components";
import { ReactionKind } from "client/Api/Hub/common";
import { ReactionsButton } from "client/Features/Reactions/ReactionsButton/ReactionsButton";
import {
	registerBirthdayWishReaction,
	unregisterBirthdayWishReaction,
	updateBirthdayWishReaction
} from "client/Api/Birthdays/birthdayWish";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	isNil,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { useAnalyticsStore } from "client/Features/Analytics/analyticsStore";
import { AnalyticsAppEventData } from "client/Api/Analytics/analytics";
import { AppType } from "client/Api/Apps/common";
import { BirthdayWishNotificationDataViewModel } from "client/Api/Notifications/notifications";

export type BirthdayWishActivityProps = {
	data: BirthdayWishNotificationDataViewModel;
	onActivityChanged: () => void;
};

export const BirthdayWishActivity = (props: BirthdayWishActivityProps): ReactElement => {
	const {
		data,
		onActivityChanged
	} = props;

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();

	const {
		birthdayWishId,
		firstname,
		lastname,
		wish,
		currentUserReaction
	} = data;

	const fullname: string = `${firstname} ${lastname}`;
	const addAnalyticsAppEvent: (data: AnalyticsAppEventData) => void = useAnalyticsStore(x => x.addAppEvent);

	const reactionClickHandler = useCallback(
		async (reaction: ReactionKind): Promise<void> => {
			addAnalyticsAppEvent({
				appType: AppType.Birthday
			});

			try {
				if (isNil(birthdayWishId)) {
					return;
				}

				if (currentUserReaction === ReactionKind.NoReaction) {
					if (reaction === ReactionKind.NoReaction) {
						throw new Error(`reaction cannot be of type ${ReactionKind.NoReaction}`);
					}

					await registerBirthdayWishReaction(
						birthdayWishId,
						reaction,
						globalCancellation
					);
				} else if (reaction === currentUserReaction) {
					await unregisterBirthdayWishReaction(birthdayWishId, globalCancellation);
				} else {
					await updateBirthdayWishReaction(
						birthdayWishId,
						reaction,
						globalCancellation
					);
				}

				onActivityChanged();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(
					localize("_GeneralErrorMessage")
				);
			}
		},
		[
			currentUserReaction,
			globalCancellation,
			birthdayWishId,
			alertManager,
			onActivityChanged,
			addAnalyticsAppEvent
		]
	);

	return (
		<>
			<span {...setTrackingId("title")}>
				<FormattedText
					text={localize("_XSentYouBirthdayWishes")}
					thingsToReplace={[
						{
							what: "{name}",
							filler: (
								<Typography
									trackingId="user-name"
									weight="bold"
								>
									{fullname}
								</Typography>
							)
						}
					]}
				/>
				<Space />
				<Typography
					trackingId="message"
					weight="bold"
				>
					{wish}
				</Typography>
			</span>
			{isNotNil(birthdayWishId) && (
				<TransparentButtonList responsiveBehavior="wrap">
					<ReactionsButton
						userReaction={currentUserReaction}
						onReactionClick={reactionClickHandler}
						bubbleClick={false}
					/>
				</TransparentButtonList>
			)}
		</>
	);
};

BirthdayWishActivity.displayName = "BirthdayWishActivity";